/**
 * GestioneScommessePage — admin (Andrea 2026-06-13).
 *
 * L'admin sceglie una giornata da giocare e rende "di cartello" alcune
 * partite. Per ogni partita può attivare il banco esterno (quote 1/X/2).
 * Le scommesse P2P (tra i 2 presidenti) sono sempre possibili sui cartelli.
 *
 * Le scommesse si chiudono all'inizio della giornata (15 min prima della
 * prima partita SA, come la formazione): mostriamo lo stato chiusa/aperta.
 *
 * Andrea 2026-09-11: "Genera quote" chiede al backend le quote 1/X/2
 * calcolate come un bookmaker (rose, fantamedie, probabili, andamento) e
 * le precompila nelle righe: l'admin sceglie quali partite rendere di
 * cartello e ritocca le quote a mano prima di confermare. Sui cartelli
 * gia' esistenti compare "Usa queste quote".
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAuthStore } from "../../lib/auth-store";
import { MobileShell } from "../shell/MobileShell";
import { useStagioneAttiva } from "../../hooks/useStagione";
import { useSquadre } from "../../hooks/useFase7";
import {
  useCompetizioniStagione,
  usePartiteCompetizione,
  type CompetizioneItem,
} from "../../hooks/useFase5";
import {
  useCartello,
  useCreaCartello,
  useAggiornaCartello,
  useEliminaCartello,
  useScommesseCartello,
  useGeneraQuote,
  type CartelloItem,
  type QuotaProposta,
  type QuoteProposteResponse,
} from "../../hooks/useScommesse";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

interface GiornataLight {
  id: string;
  stagione_id: string;
  numero: number;
  numero_serie_a: number;
  stato: string;
}

function errMsg(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err.response?.data?.detail ?? err.message ?? tr("Errore");
}

function deadlineLabel(c: CartelloItem): string {
  if (!c.deadline) return tr("orario prima partita non disponibile");
  const d = new Date(c.deadline);
  return d.toLocaleString(localeCorrente(), {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function GestioneScommessePage() {
  const { data: stagione } = useStagioneAttiva();
  const stagioneId = stagione?.id ?? null;
  const isAuth = useAuthStore((s) => !!s.accessToken);

  const { data: giornate } = useQuery({
    queryKey: ["stagione-giornate", stagioneId],
    queryFn: async () =>
      (await api.get<GiornataLight[]>(`/admin/stagioni/${stagioneId}/giornate`)).data,
    enabled: isAuth && !!stagioneId,
  });

  const giornateDaGiocare = useMemo(
    () =>
      (giornate ?? []).filter((g) => g.stato === "da_giocare").sort((a, b) => a.numero - b.numero),
    [giornate],
  );

  const [giornataId, setGiornataId] = useState<string | null>(null);
  const giornataSel =
    giornateDaGiocare.find((g) => g.id === giornataId) ?? giornateDaGiocare[0] ?? null;
  const giornataSelId = giornataSel?.id ?? null;

  const { data: competizioni } = useCompetizioniStagione(stagioneId);
  const competizioniAttive = (competizioni ?? []).filter((c) => c.stato !== "conclusa");

  const { data: cartelli } = useCartello({
    stagioneId,
    giornataId: giornataSelId,
  });
  const cartelliGiornata = cartelli ?? [];
  const partiteGiaCartello = useMemo(
    () => new Set(cartelliGiornata.map((c) => c.partita_id)),
    [cartelliGiornata],
  );

  // Quote generate per la giornata selezionata (null = non ancora chieste).
  const genera = useGeneraQuote();
  const [quote, setQuote] = useState<QuoteProposteResponse | null>(null);
  const [erroreQuote, setErroreQuote] = useState<string | null>(null);
  useEffect(() => {
    setQuote(null);
    setErroreQuote(null);
  }, [giornataSelId]);
  const proposte = useMemo(() => {
    const m = new Map<string, QuotaProposta>();
    for (const p of quote?.proposte ?? []) m.set(p.partita_id, p);
    return m;
  }, [quote]);
  async function generaQuote() {
    if (!giornataSelId) return;
    setErroreQuote(null);
    try {
      setQuote(await genera.mutateAsync({ giornataId: giornataSelId }));
    } catch (e) {
      setErroreQuote(errMsg(e));
    }
  }

  return (
    <MobileShell logoSuffix="SCOMMESSE">
      <div className="bg-gradient-to-br from-bg-card to-primary-deep border border-accent/30 rounded-card p-4 flex items-center gap-3">
        <div className="text-3xl">🎰</div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base text-accent tracking-wider">
            {tr("SQUAD BET · ADMIN")}
          </div>
          <div className="text-xs text-fg-muted">
            {tr("Rendi scommettibili le partite · quote banco 1/X/2")}
          </div>
        </div>
      </div>

      {!stagioneId && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-6 text-center text-fg-muted">
          {tr("Nessuna stagione attiva.")}
        </div>
      )}

      {stagioneId && giornateDaGiocare.length === 0 && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-6 text-center text-fg-muted">
          {tr("Nessuna giornata da giocare: niente da rendere scommettibile.")}
        </div>
      )}

      {stagioneId && giornateDaGiocare.length > 0 && (
        <>
          {/* Selettore giornata */}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-wider text-fg-muted">
              {tr("Giornata")}
            </label>
            <select
              value={giornataSelId ?? ""}
              onChange={(e) => setGiornataId(e.target.value)}
              className="mt-1 w-full bg-bg-card border border-primary/30 rounded-card px-3 py-2 text-sm text-fg focus:border-accent/60 outline-none"
            >
              {giornateDaGiocare.map((g) => (
                <option key={g.id} value={g.id}>
                  Giornata {g.numero} (SA {g.numero_serie_a})
                </option>
              ))}
            </select>
          </div>

          {/* Genera quote: un calcolo per tutta la giornata, poi ogni riga
              (nuova o cartello esistente) le propone precompilate. */}
          <div className="bg-bg-card border border-accent/30 rounded-card p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="font-display text-sm text-accent tracking-wider">
                  {tr("🎲 QUOTE DA BOOKMAKER")}
                </div>
                <div className="text-[11px] text-fg-muted">
                  {tr(
                    "Calcolate da rose, fantamedie, probabili formazioni e andamento (margine banco 8%). Poi scegli tu quali partite rendere di cartello e correggi le quote a mano.",
                  )}
                </div>
              </div>
              <button
                onClick={generaQuote}
                disabled={genera.isPending || !giornataSelId}
                className="shrink-0 text-xs font-bold bg-accent text-ink rounded-card px-3 py-2 hover:bg-accent/90 transition disabled:opacity-50"
              >
                {genera.isPending
                  ? tr("Calcolo…")
                  : quote
                    ? tr("Ricalcola quote")
                    : tr("Genera quote")}
              </button>
            </div>
            {quote && (
              <div className="text-[10px] text-fg-muted font-mono">
                Giornata {quote.numero} (SA {quote.numero_serie_a}) · {quote.proposte.length}{" "}
                partite · voti in stagione: {quote.n_voti}
                {quote.n_voti === 0 ? tr(" (forza dai soli valori Transfermarkt)") : ""} ·
                probabili: {quote.n_probabili} giocatori
              </div>
            )}
            {erroreQuote && <div className="text-[11px] text-danger">⚠ {erroreQuote}</div>}
          </div>

          {/* Cartelli già attivi per la giornata */}
          <div>
            <h3 className="font-display text-lg tracking-wider text-fg mb-2">
              {tr("PARTITE DI CARTELLO")}
            </h3>
            {cartelliGiornata.length === 0 ? (
              <div className="bg-bg-card border border-primary/20 rounded-card p-4 text-center text-sm text-fg-muted">
                {tr("Nessuna partita di cartello per questa giornata. Aggiungine una qui sotto.")}
              </div>
            ) : (
              <div className="space-y-2">
                {cartelliGiornata.map((c) => (
                  <CartelloAdminCard
                    key={c.id}
                    cartello={c}
                    proposta={proposte.get(c.partita_id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Aggiungi partite (una sezione per competizione attiva) */}
          <div>
            <h3 className="font-display text-lg tracking-wider text-fg mb-2">
              {tr("AGGIUNGI PARTITA")}
            </h3>
            {competizioniAttive.length === 0 ? (
              <div className="bg-bg-card border border-primary/20 rounded-card p-4 text-center text-sm text-fg-muted">
                {tr("Nessuna competizione attiva.")}
              </div>
            ) : (
              <div className="space-y-3">
                {competizioniAttive.map((comp) => (
                  <CompetizionePartiteAggiungi
                    key={comp.id}
                    competizione={comp}
                    giornataId={giornataSelId}
                    partiteGiaCartello={partiteGiaCartello}
                    proposte={proposte}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </MobileShell>
  );
}

// ============================================================
// Card di un cartello esistente (modifica banco/quote/stato/elimina)
// ============================================================
function CartelloAdminCard({
  cartello,
  proposta,
}: {
  cartello: CartelloItem;
  proposta?: QuotaProposta;
}) {
  const c = cartello;
  const aggiorna = useAggiornaCartello();
  const elimina = useEliminaCartello();
  const { data: scommesse } = useScommesseCartello(c.id);

  const [banco, setBanco] = useState(c.banco_attivo);
  const [q1, setQ1] = useState(c.quota_1 ?? "");
  const [qx, setQx] = useState(c.quota_x ?? "");
  const [q2, setQ2] = useState(c.quota_2 ?? "");
  const [errore, setErrore] = useState<string | null>(null);

  const nP2P =
    scommesse?.p2p.filter((s) => ["proposta", "accettata"].includes(s.stato)).length ?? 0;
  const nBanco = scommesse?.banco.filter((s) => s.stato === "piazzata").length ?? 0;
  const haScommesse = nP2P > 0 || nBanco > 0;

  async function salvaQuote() {
    setErrore(null);
    try {
      await aggiorna.mutateAsync({
        cartelloId: c.id,
        bancoAttivo: banco,
        quota1: banco && q1 !== "" ? Number(q1) : null,
        quotaX: banco && qx !== "" ? Number(qx) : null,
        quota2: banco && q2 !== "" ? Number(q2) : null,
      });
    } catch (e) {
      setErrore(errMsg(e));
    }
  }

  async function cambiaStato(stato: "aperta" | "chiusa") {
    setErrore(null);
    try {
      await aggiorna.mutateAsync({ cartelloId: c.id, stato });
    } catch (e) {
      setErrore(errMsg(e));
    }
  }

  async function rimuovi() {
    setErrore(null);
    try {
      await elimina.mutateAsync(c.id);
    } catch (e) {
      setErrore(errMsg(e));
    }
  }

  const statoBadge =
    c.stato === "risolta"
      ? { txt: "RISOLTA", cls: "bg-info/15 text-info border-info/40" }
      : c.chiusa
        ? { txt: tr("CHIUSA"), cls: "bg-danger/15 text-danger border-danger/40" }
        : { txt: tr("APERTA"), cls: "bg-success/15 text-success border-success/40" };

  return (
    <div className="bg-bg-card border border-primary/20 rounded-card p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="font-display text-sm text-fg truncate">
          {c.squadra_casa_nome} <span className="text-fg-muted">vs</span> {c.squadra_trasferta_nome}
        </div>
        <span
          className={`shrink-0 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-pill border ${statoBadge.cls}`}
        >
          {statoBadge.txt}
        </span>
      </div>

      <div className="text-[10px] text-fg-muted font-mono mb-2">
        Chiusura: {deadlineLabel(c)} · P2P attive: {nP2P} · banco: {nBanco}
      </div>

      {proposta && c.stato !== "risolta" && (
        <PropostaBox
          proposta={proposta}
          azione="Usa queste quote"
          onUsa={() => {
            setBanco(true);
            setQ1(proposta.quota_1);
            setQx(proposta.quota_x);
            setQ2(proposta.quota_2);
          }}
        />
      )}

      {/* Banco esterno + quote */}
      {c.stato !== "risolta" && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-xs text-fg">
            <input
              type="checkbox"
              checked={banco}
              onChange={(e) => setBanco(e.target.checked)}
              className="accent-accent"
            />
            {tr("Banco esterno attivo (quote 1/X/2 per i non coinvolti)")}
          </label>
          {banco && (
            <div className="grid grid-cols-3 gap-2">
              <QuotaInput label="1" value={q1} onChange={setQ1} />
              <QuotaInput label="X" value={qx} onChange={setQx} />
              <QuotaInput label="2" value={q2} onChange={setQ2} />
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={salvaQuote}
              disabled={aggiorna.isPending}
              className="text-xs font-bold bg-accent/15 text-accent border border-accent/40 rounded-card px-3 py-1.5 hover:bg-accent/25 transition disabled:opacity-50"
            >
              {tr("Salva")}
            </button>
            {!c.chiusa ? (
              <button
                onClick={() => cambiaStato("chiusa")}
                disabled={aggiorna.isPending}
                className="text-xs font-bold bg-danger/10 text-danger border border-danger/30 rounded-card px-3 py-1.5 hover:bg-danger/20 transition disabled:opacity-50"
              >
                {tr("Chiudi scommesse")}
              </button>
            ) : (
              <button
                onClick={() => cambiaStato("aperta")}
                disabled={aggiorna.isPending}
                className="text-xs font-bold bg-success/10 text-success border border-success/30 rounded-card px-3 py-1.5 hover:bg-success/20 transition disabled:opacity-50"
              >
                {tr("Riapri")}
              </button>
            )}
            {!haScommesse && (
              <button
                onClick={rimuovi}
                disabled={elimina.isPending}
                className="text-xs font-bold text-fg-muted border border-primary/20 rounded-card px-3 py-1.5 hover:bg-bg-card-hover transition disabled:opacity-50"
              >
                {tr("Elimina")}
              </button>
            )}
          </div>
        </div>
      )}

      {errore && <div className="text-[11px] text-danger mt-2">⚠ {errore}</div>}
    </div>
  );
}

function QuotaInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] text-fg-muted text-center">Quota {label}</span>
      <input
        type="number"
        step="0.05"
        min="1.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="—"
        className="w-full bg-bg-card border border-primary/30 rounded-card px-2 py-1.5 text-sm text-fg text-center tabular-nums focus:border-accent/60 outline-none"
      />
    </label>
  );
}

// ============================================================
// Partite di una competizione da rendere di cartello
// ============================================================
function CompetizionePartiteAggiungi({
  competizione,
  giornataId,
  partiteGiaCartello,
  proposte,
}: {
  competizione: CompetizioneItem;
  giornataId: string | null;
  partiteGiaCartello: Set<string>;
  proposte: Map<string, QuotaProposta>;
}) {
  const { data: partite } = usePartiteCompetizione(competizione.id);
  const { data: squadre } = useSquadre();
  const nomeSquadra = (id: string): string =>
    squadre?.find((s) => s.id === id)?.nome ?? id.slice(0, 8);

  const disponibili = (partite ?? []).filter(
    (p) => p.giornata_id === giornataId && !p.obsoleto && !partiteGiaCartello.has(p.id),
  );

  if (giornataId === null) return null;
  if (disponibili.length === 0) return null;

  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-fg-muted mb-1">
        {competizione.nome}
      </div>
      <div className="space-y-2">
        {disponibili.map((p) => (
          <AggiungiPartitaRow
            key={p.id}
            partitaId={p.id}
            casa={nomeSquadra(p.squadra_casa_id)}
            trasferta={nomeSquadra(p.squadra_trasferta_id)}
            proposta={proposte.get(p.id)}
          />
        ))}
      </div>
    </div>
  );
}

function AggiungiPartitaRow({
  partitaId,
  casa,
  trasferta,
  proposta,
}: {
  partitaId: string;
  casa: string;
  trasferta: string;
  proposta?: QuotaProposta;
}) {
  const crea = useCreaCartello();
  const [banco, setBanco] = useState(false);
  const [q1, setQ1] = useState("");
  const [qx, setQx] = useState("");
  const [q2, setQ2] = useState("");
  const [errore, setErrore] = useState<string | null>(null);

  // Quote generate: precompilano i campi (restano modificabili) e
  // accendono il banco. Rendere di cartello resta una scelta per riga.
  useEffect(() => {
    if (!proposta) return;
    setBanco(true);
    setQ1(proposta.quota_1);
    setQx(proposta.quota_x);
    setQ2(proposta.quota_2);
  }, [proposta]);

  async function aggiungi() {
    setErrore(null);
    try {
      await crea.mutateAsync({
        partitaId,
        bancoAttivo: banco,
        quota1: banco && q1 !== "" ? Number(q1) : null,
        quotaX: banco && qx !== "" ? Number(qx) : null,
        quota2: banco && q2 !== "" ? Number(q2) : null,
      });
    } catch (e) {
      setErrore(errMsg(e));
    }
  }

  return (
    <div className="bg-bg-card border border-primary/20 rounded-card p-3">
      <div className="font-display text-sm text-fg truncate mb-2">
        {casa} <span className="text-fg-muted">vs</span> {trasferta}
      </div>
      {proposta && <PropostaBox proposta={proposta} />}
      <label className="flex items-center gap-2 text-xs text-fg mb-2">
        <input
          type="checkbox"
          checked={banco}
          onChange={(e) => setBanco(e.target.checked)}
          className="accent-accent"
        />
        {tr("Attiva banco esterno (quote 1/X/2)")}
      </label>
      {banco && (
        <div className="grid grid-cols-3 gap-2 mb-2">
          <QuotaInput label="1" value={q1} onChange={setQ1} />
          <QuotaInput label="X" value={qx} onChange={setQx} />
          <QuotaInput label="2" value={q2} onChange={setQ2} />
        </div>
      )}
      <button
        onClick={aggiungi}
        disabled={crea.isPending}
        className="text-xs font-bold bg-accent/15 text-accent border border-accent/40 rounded-card px-3 py-1.5 hover:bg-accent/25 transition disabled:opacity-50"
      >
        {tr("➕ Rendi scommettibile")}
      </button>
      {errore && <div className="text-[11px] text-danger mt-2">⚠ {errore}</div>}
    </div>
  );
}

// ============================================================
// Riquadro con la proposta del bookmaker per una partita
// ============================================================
function PropostaBox({
  proposta,
  azione,
  onUsa,
}: {
  proposta: QuotaProposta;
  azione?: string;
  onUsa?: () => void;
}) {
  const pct = (p: number) => `${Math.round(p * 100)}%`;
  const riga = (s: QuotaProposta["casa"]) => (
    <div className="text-[10px] text-fg-muted truncate">
      <span className="text-fg">{s.nome}</span> · attesa {s.totale_atteso.toFixed(1)}
      {s.forma.length > 0
        ? tr(" · ultime {{v1}}", { v1: s.forma.map((x) => x.toFixed(0)).join("/") })
        : ""}
      {" · "}
      {s.da_formazione ? tr("formazione consegnata") : tr("undici stimato")}
      {s.assenti.length > 0
        ? tr(" · a rischio: {{v1}}", { v1: s.assenti.slice(0, 3).join(", ") })
        : ""}
    </div>
  );
  return (
    <div className="mb-2 rounded-card border border-accent/30 bg-accent/5 p-2 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
          {tr("🎲 Proposta bookmaker")}
        </div>
        <div className="font-mono text-[11px] text-fg tabular-nums">
          1 {proposta.quota_1} <span className="text-fg-muted">({pct(proposta.p1)})</span> · X{" "}
          {proposta.quota_x} <span className="text-fg-muted">({pct(proposta.px)})</span> · 2{" "}
          {proposta.quota_2} <span className="text-fg-muted">({pct(proposta.p2)})</span>
        </div>
      </div>
      {riga(proposta.casa)}
      {riga(proposta.trasferta)}
      {azione && onUsa && (
        <button
          type="button"
          onClick={onUsa}
          className="text-[11px] font-bold text-accent underline underline-offset-2 hover:opacity-80"
        >
          {azione}
        </button>
      )}
    </div>
  );
}
